import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircle2, AlertTriangle, ExternalLink, RefreshCw, Send, Receipt,
  Loader2, FileText, Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { api } from "../lib/api";

const DOC_TITLE = {
  transaction_account: { title: "מסמכים שנשלחו · חשבון עסקה", icon: FileText, color: "text-emerald-700", accent: "bg-emerald-50 border-emerald-200" },
  tax_invoice: { title: "מסמכים שנשלחו · חשבונית מס", icon: Receipt, color: "text-blue-700", accent: "bg-blue-50 border-blue-200" },
  tax_invoice_receipt: { title: "מסמכים שנשלחו · חשבונית מס / קבלה", icon: Receipt, color: "text-purple-700", accent: "bg-purple-50 border-purple-200" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function SentDocuments() {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get("company");
  const docType = searchParams.get("doc") || "transaction_account";
  const meta = DOC_TITLE[docType] || DOC_TITLE.transaction_account;

  const [company, setCompany] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState({}); // {msg_id: "paid" | "resend"}
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        api.get("/companies"),
        api.get(`/companies/${companyId}/sent-documents`, { params: { doc_type: docType } }),
      ]);
      const c = (cRes.data?.companies || []).find((c) => c.id === companyId);
      setCompany(c || null);
      setItems(sRes.data?.items || []);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בטעינת המסמכים");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((m) => m.id)));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`למחוק ${selectedIds.size} רשומות? פעולה זו לא תמחק את המסמכים בחשבונית ירוקה — רק את ההיסטוריה כאן.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/sent-documents/bulk-delete`, {
        message_ids: [...selectedIds],
      });
      toast.success(`נמחקו ${data.deleted} רשומות`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה במחיקה");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId, docType]);

  const handleMarkPaid = async (msg) => {
    if (!window.confirm(`לסמן את ${msg.customer_name} כשולם ולשלוח חשבונית מס/קבלה?`)) return;
    setActionBusy((s) => ({ ...s, [msg.id]: "paid" }));
    try {
      const { data } = await api.post(`/companies/${companyId}/messages/${msg.id}/mark-paid`);
      if (data.ok) {
        toast.success(`נשלחה חשבונית מס/קבלה מס׳ ${data.invoice_number || ""} ל-${msg.customer_name}`);
      } else {
        toast.error(data.invoice_error || data.email_error || "שגיאה בשליחה");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בסימון כשולם");
    } finally {
      setActionBusy((s) => { const n = { ...s }; delete n[msg.id]; return n; });
    }
  };

  const handleResend = async (msg) => {
    if (!window.confirm(`לשלוח שוב את ${meta.title.split("·")[1]?.trim() || "המסמך"} ל-${msg.customer_name}?`)) return;
    setActionBusy((s) => ({ ...s, [msg.id]: "resend" }));
    try {
      const { data } = await api.post(`/companies/${companyId}/messages/${msg.id}/resend`);
      if (data.ok) {
        toast.success(`נשלחה שוב חשבונית מס׳ ${data.invoice_number || ""} ל-${msg.customer_name}`);
      } else {
        toast.error(data.invoice_error || data.email_error || "שגיאה בשליחה");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בשליחה חוזרת");
    } finally {
      setActionBusy((s) => { const n = { ...s }; delete n[msg.id]; return n; });
    }
  };

  const Icon = meta.icon;

  return (
    <div className="min-h-screen bg-stone-50">
      <Toaster position="top-center" richColors />
      <main className="p-6 lg:p-10 max-w-6xl mx-auto">
        <div className={`rounded-2xl border-2 p-5 mb-6 ${meta.accent}`} data-testid="sent-header">
          <div className="flex items-center gap-3">
            <Icon className={`h-6 w-6 ${meta.color}`} />
            <div>
              <h1 className={`text-xl font-heading font-bold ${meta.color}`}>{meta.title}</h1>
              <p className="text-xs text-stone-600 mt-0.5">
                {company ? company.name : "—"} · {items.length} מסמכים
              </p>
            </div>
            <Button
              onClick={load}
              variant="outline"
              size="sm"
              className="mr-auto"
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-3.5 w-3.5 ml-1" /> רענן
            </Button>
          </div>
        </div>

        {/* Bulk actions toolbar - appears when items exist */}
        {items.length > 0 && (
          <div className="bg-white rounded-xl border border-stone-200 px-4 py-2.5 mb-3 flex items-center gap-3 flex-wrap" data-testid="bulk-toolbar">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={selectedIds.size === items.length && items.length > 0}
                onChange={toggleSelectAll}
                className="h-4 w-4 rounded accent-stone-900"
                data-testid="select-all-checkbox"
              />
              <span className="text-xs font-semibold text-stone-700">
                {selectedIds.size > 0 ? `${selectedIds.size} נבחרו` : "בחרי הכול"}
              </span>
            </label>
            {selectedIds.size > 0 && (
              <Button
                onClick={handleBulkDelete}
                disabled={deleting}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white h-7 text-xs mr-auto"
                data-testid="bulk-delete-btn"
              >
                {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Trash2 className="h-3.5 w-3.5 ml-1" /> מחקי {selectedIds.size} רשומות</>}
              </Button>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center" data-testid="empty-state">
            <FileText className="h-10 w-10 mx-auto text-stone-300 mb-3" />
            <p className="text-stone-600 font-semibold">עדיין לא נשלחו מסמכים מסוג זה</p>
            <p className="text-xs text-stone-400 mt-1">ברגע שתשלחי — המסמכים יופיעו כאן.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="sent-list">
            {items.map((msg) => (
              <div
                key={msg.id}
                className={`bg-white rounded-xl border p-4 hover:shadow-sm transition-shadow ${selectedIds.has(msg.id) ? "border-red-300 bg-red-50/40" : "border-stone-200"}`}
                data-testid={`sent-row-${msg.id}`}
              >
                <div className="grid grid-cols-[auto,1fr,auto] gap-4 items-start">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedIds.has(msg.id)}
                    onChange={() => toggleSelect(msg.id)}
                    className="h-4 w-4 rounded accent-red-600 mt-1 flex-shrink-0"
                    data-testid={`select-${msg.id}`}
                  />
                  {/* Left: customer + doc info */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-stone-900">{msg.customer_name}</span>
                      <span className="text-xs text-stone-400">·</span>
                      <span className="text-xs text-stone-600" dir="ltr">{msg.customer_email || msg.customer_phone}</span>
                    </div>

                    <div className="flex items-center gap-4 flex-wrap text-xs text-stone-600">
                      <span><strong className="text-stone-900">{(msg.amount || 0).toFixed(2)}₪</strong></span>
                      <span>·</span>
                      <span>{msg.description || "—"}</span>
                      <span>·</span>
                      <span>{fmtDate(msg.created_at)}</span>
                      {msg.paid && (
                        <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full font-bold">
                          ✓ שולם
                        </span>
                      )}
                    </div>

                    {/* Per-channel delivery status rows */}
                    <div className="space-y-1 pt-1">
                      {/* WhatsApp row - shown if this message was whatsapp-initiated OR has a phone */}
                      {(msg.channel === "whatsapp" || msg.whatsapp_sid || msg.whatsapp_error) && (
                        <div className={`flex items-start gap-2 text-[11px] rounded-md px-2 py-1.5 ${msg.whatsapp_ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`} data-testid={`wa-status-${msg.id}`}>
                          <span className="text-sm flex-shrink-0">📱</span>
                          <div className="flex-1">
                            <span className="font-bold">וואטסאפ:</span>{" "}
                            {msg.whatsapp_ok ? (
                              <span>נשלח בהצלחה {msg.customer_phone ? `ל-${msg.customer_phone}` : ""}</span>
                            ) : (
                              <span>{msg.whatsapp_error || "נכשל"}</span>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Email row - shown if message has an email recipient */}
                      {msg.customer_email && (msg.channel !== "whatsapp" || msg.email_ok || msg.email_error) && (
                        <div className={`flex items-start gap-2 text-[11px] rounded-md px-2 py-1.5 ${msg.email_ok ? "bg-green-50 text-green-800" : "bg-stone-50 text-stone-600"}`} data-testid={`email-status-${msg.id}`}>
                          <span className="text-sm flex-shrink-0">📧</span>
                          <div className="flex-1">
                            <span className="font-bold">אימייל:</span>{" "}
                            {msg.email_ok ? (
                              <span>נשלח בהצלחה ל-<span dir="ltr">{msg.customer_email}</span></span>
                            ) : msg.email_error ? (
                              <span>{msg.email_error}</span>
                            ) : (
                              <span className="opacity-60">לא נשלח (לא סומן "גם מייל")</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      {msg.invoice_url ? (
                        <a
                          href={msg.invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs bg-stone-900 text-white px-2 py-1 rounded-md hover:bg-stone-700 transition"
                          data-testid={`doc-link-${msg.id}`}
                        >
                          <ExternalLink className="h-3 w-3" />
                          מסמך #{msg.invoice_number}
                        </a>
                      ) : (
                        <span className="text-xs text-red-700 bg-red-50 px-2 py-1 rounded-md">
                          ⚠️ {msg.invoice_error || "לא נוצר מסמך"}
                        </span>
                      )}
                      {msg.paid && msg.paid_invoice_url && (
                        <a
                          href={msg.paid_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs bg-emerald-600 text-white px-2 py-1 rounded-md hover:bg-emerald-700 transition"
                          data-testid={`paid-doc-link-${msg.id}`}
                        >
                          <Receipt className="h-3 w-3" />
                          חשבונית מס/קבלה #{msg.paid_invoice_number}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Right: actions (only for transaction_account) */}
                  {docType === "transaction_account" && (
                    <div className="flex lg:flex-col gap-2 shrink-0">
                      {!msg.paid && (
                        <Button
                          onClick={() => handleMarkPaid(msg)}
                          disabled={!!actionBusy[msg.id]}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs whitespace-nowrap"
                          data-testid={`mark-paid-${msg.id}`}
                        >
                          {actionBusy[msg.id] === "paid" ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <><Receipt className="h-3.5 w-3.5 ml-1" /> סמן כשולם + חשבונית מס/קבלה</>
                          )}
                        </Button>
                      )}
                      <Button
                        onClick={() => handleResend(msg)}
                        disabled={!!actionBusy[msg.id]}
                        variant="outline"
                        className="h-8 text-xs whitespace-nowrap"
                        data-testid={`resend-${msg.id}`}
                      >
                        {actionBusy[msg.id] === "resend" ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <><Send className="h-3.5 w-3.5 ml-1" /> שלח שוב</>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
